/**
 * Supplier Search API Service
 * API calls for supplier search operations
 */

import { apiClient } from '@/auth';
import type { ApiResponse } from '@http';
import type { SupplierSearchTask, SupplierSearchTasksResponse, ProductSuppliersResponse } from '../types/supplierSearch.types';
import type { Supplier } from '../types';

export class SupplierSearchApiService {
  /**
   * Start supplier search for a product
   * @param productId - Product ID to search suppliers for
   * @returns Promise<SupplierSearchTask> - Created search task
   */
  static async startSupplierSearch(productId: number): Promise<SupplierSearchTask> {
    try {
      const response: ApiResponse<SupplierSearchTask> = await apiClient.post<SupplierSearchTask>(
        `alibaba-search/products/${productId}/search-suppliers`,
        {}
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to start supplier search for product ${productId}:`, error);
      throw error;
    }
  }

  /**
   * Get supplier search task status
   * @param taskId - Search task ID
   * @returns Promise<SupplierSearchTask> - Search task with current status
   */
  static async getSearchStatus(taskId: string): Promise<SupplierSearchTask> {
    try {
      const response: ApiResponse<SupplierSearchTask> = await apiClient.get<SupplierSearchTask>(
        `alibaba-search/supplier-searches/${taskId}`
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to get search status for task ${taskId}:`, error);
      throw error;
    }
  }

  /**
   * Get all supplier search tasks for a product
   * @param productId - Product ID
   * @returns Promise<SupplierSearchTasksResponse> - List of search tasks
   */
  static async getProductSearchTasks(productId: number): Promise<SupplierSearchTasksResponse> {
    try {
      const response: ApiResponse<SupplierSearchTasksResponse> = await apiClient.get<SupplierSearchTasksResponse>(
        `alibaba-search/products/${productId}/supplier-searches`
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to get search tasks for product ${productId}:`, error);
      throw error;
    }
  }

  /**
   * Get suppliers found by a search task
   * @param productId - Search product ID
   * @returns Promise<ProductSuppliersResponse> - List of suppliers
   */
  static async getProductSuppliers(productId: string | number ): Promise<ProductSuppliersResponse> {
    try {
      const response: ApiResponse<ProductSuppliersResponse> = await apiClient.get<ProductSuppliersResponse>(
        `products/${productId}/suppliers`
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to get suppliers for product ${productId}:`, error);
      throw error;
    }
  }

    /**
   * Get suppliers found by a search task
   * @param taskId - Search task ID
   * @returns Promise<ProductSuppliersResponse> - List of suppliers
   */
  static async getTaskSuppliers(taskId: string): Promise<ProductSuppliersResponse> {
    try {
      const response: ApiResponse<ProductSuppliersResponse> = await apiClient.get<ProductSuppliersResponse>(
        `alibaba-search/supplier-searches/${taskId}/suppliers`
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to get suppliers for task ${taskId}:`, error);
      throw error;
    }
  }

  /**
   * Create manual supplier for a product
   * POST /products/{product_id}/suppliers
   * @param productId - Product ID
   * @param supplierData - Supplier data (name, email required; supplier_info, website, phone, wechat_number optional)
   * @returns Promise<Supplier> - Created supplier
   */
  static async createManualSupplier(
    productId: string | number,
    supplierData: {
      name: string;
      email: string;
      supplier_info?: string;
      website?: string;
      phone?: string;
      wechat_number?: string;
    }
  ): Promise<Supplier> {
    try {
      const response: ApiResponse<Supplier> = await apiClient.post<Supplier>(
        `products/${productId}/suppliers`,
        supplierData
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to create manual supplier for product ${productId}:`, error);
      throw error;
    }
  }

  /**
   * Update manual supplier
   * PUT /products/{product_id}/suppliers/{supplier_id}
   * Only manual suppliers (is_manual=true) can be updated
   * @param productId - Product ID
   * @param supplierId - Supplier ID
   * @param supplierData - Updated supplier data
   * @returns Promise<Supplier> - Updated supplier
   */
  static async updateManualSupplier(
    productId: string | number,
    supplierId: string | number,
    supplierData: Partial<{
      name: string;
      email: string;
      supplier_info: string;
      website: string;
      phone: string;
      wechat_number: string;
    }>
  ): Promise<Supplier> {
    try {
      const response: ApiResponse<Supplier> = await apiClient.put<Supplier>(
        `products/${productId}/suppliers/${supplierId}`,
        supplierData
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to update supplier ${supplierId} for product ${productId}:`, error);
      throw error;
    }
  }

  /**
   * Delete manual supplier
   * DELETE /products/{product_id}/suppliers/{supplier_id}
   * Only manual suppliers (is_manual=true) can be deleted
   * @param productId - Product ID
   * @param supplierId - Supplier ID
   * @returns Promise<void>
   */
  static async deleteManualSupplier(
    productId: string | number,
    supplierId: string | number
  ): Promise<void> {
    try {
      await apiClient.delete(
        `products/${productId}/suppliers/${supplierId}`
      );
    } catch (error) {
      console.error(`Failed to delete supplier ${supplierId} for product ${productId}:`, error);
      throw error;
    }
  }
}

